import React from 'react';
import { validated } from 'react-custom-validation'
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import moment from 'moment';

import { handleCheckboxChange, handleDateChange, handleMultiSelectChange, handleTextChange } from 'utils/handle-changes';
import { isRequired } from 'utils/validations';

import Button from 'components/Button';
import Datepicker from 'components/Datepicker';
import Dropdown from 'components/Dropdown';
import Form from 'components/Form';
import FormColumn from 'components/FormColumn';
import FormRow from 'components/FormRow';
import FormValidationMessage from 'components/FormValidationMessage';
import Label from 'components/Label';
import LoadingCircle from 'components/LoadingCircle';
import Radio from 'components/Radio';
import RadioGroup from 'components/RadioGroup';
import Textbox from 'components/Textbox';
import ToggleSwitch from 'components/ToggleSwitch';

class EditPostingForm extends React.Component {
    static propTypes = {
        error: PropTypes.object.isRequired,
        handleCancel: PropTypes.func.isRequired,
        industryExperienceTypes: PropTypes.array.isRequired,
        isSaving: PropTypes.bool.isRequired,
        onInvalid: PropTypes.func.isRequired,
        onValid: PropTypes.func.isRequired,
        onInvalidDraft: PropTypes.func.isRequired,
        onValidDraft: PropTypes.func.isRequired,
        opportunity: PropTypes.object.isRequired,
        setOpportunity: PropTypes.func.isRequired,
        specialties: PropTypes.array.isRequired
    };

    constructor() {
        super();

        this.state = {
            editorState: EditorState.createEmpty()
        }
    }

    componentDidMount() {
        //Convert description to editor data
        this.handleEditorLoad();
    }

    componentDidUpdate(prevProps) {
        if(prevProps.opportunity.id !== this.props.opportunity.id) {
            this.handleEditorLoad();
        }
    }

    handleEditorLoad = () => {
        //Convert description to editor data
        if(this.props.opportunity.description != null) {
            const contentBlock = htmlToDraft(this.props.opportunity.description);

            console.log('contentBlock', contentBlock)
            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                console.log('contentState', contentState)
                const editorState = EditorState.createWithContent(contentState);
                console.log('editorState', editorState)
                this.setState({editorState}, () => {
                    console.log('state', this.state);
                });
            }
        }
    }

    _handleChange(name, value) {
        let newOpportunityObj = update(this.props.opportunity, {});
        newOpportunityObj[name] = value;
        this.props.setOpportunity(newOpportunityObj);
    }

    render() {
        let { error, isSaving, onInvalid, onValid, onInvalidDraft, onValidDraft, opportunity, $field, $validation } = this.props;

        return (
            <Form className="edit-posting-form">
                <FormRow className="c-form__instructions required-fields-instructions">
                    *Required Fields
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtTitle"
                        label="Title"
                        name="title"
                        required
                        type="text"
                        validation={$validation.title}
                        value={opportunity.title || ''}
                        {...$field('title', (event) => handleTextChange(this._handleChange.bind(this), event))}
                    />
                </FormRow>

                <FormRow>
                    <Dropdown
                        closeOnSelect={false}
                        id="selRoleType"
                        label="Specialty"
                        labelKey="name"
                        multi={true}
                        name="specializations"
                        onChange={handleMultiSelectChange.bind(this, this._handleChange.bind(this), 'specializations', this.props.$fieldEvent, null)}
                        options={this.props.specialties}
                        required
                        simpleValue={false}
                        validation={$validation.specializations}
                        value={opportunity.specializations}
                        valueKey="id"
                    />
                </FormRow>

                <FormRow columnCount={2}>
                    <FormColumn>
                        <Datepicker
                            label="Start Date"
                            id="dateStartDate"
                            name="startDate"
                            placeholderText="select date"
                            required
                            validation={$validation.startDate}
                            selected={opportunity.startDate ? moment(opportunity.startDate) : null}
                            {...$field('startDate', (newDate) => handleDateChange(this._handleChange.bind(this), 'startDate', null, newDate))}
                        />
                    </FormColumn>

                    <FormColumn>
                        <Datepicker
                            label="End Date"
                            id="dateEndDate"
                            name="endDate"
                            onChange={(newDate) => handleDateChange(this._handleChange.bind(this), 'endDate', null, newDate)}
                            placeholderText="select date"
                            selected={opportunity.endDate ? moment(opportunity.endDate) : null}
                        />
                    </FormColumn>
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtDuration"
                        label="Duration"
                        name="duration"
                        onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                        type="text"
                        value={opportunity.duration || ''}
                    />
                </FormRow>

                <FormRow>
                    <Textbox
                        id="txtSummary"
                        label="Summary"
                        name="summary"
                        required
                        type="textarea"
                        validation={$validation.summary}
                        value={opportunity.summary || ''}
                        {...$field('summary', (event) => handleTextChange(this._handleChange.bind(this), event))}
                    />
                </FormRow>

                <FormRow>
                    <Label>
                        Description <span className="c-label__required-asterisk">*</span>
                    </Label>
                    <Editor
                        editorClassName="edit-posting-form__editor"
                        editorState={this.state.editorState}
                        onEditorStateChange={(editorState) => {
                            this.setState({editorState}, () => {
                                this._handleChange('description', draftToHtml(convertToRaw(this.state.editorState.getCurrentContent())))
                            });
                        }}
                    />
                    {$validation.description.show && <FormValidationMessage>{$validation.description.error.reason}</FormValidationMessage>}
                </FormRow>

                <FormRow>
                    <Label>
                        Experience Level
                    </Label>

                    <RadioGroup>
                        <Radio
                            checked={opportunity.experienceLevel === '0-3 yrs'}
                            id="chkExperienceLevel03"
                            label="0-3 yrs"
                            name="experienceLevel"
                            onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                            value="0-3 yrs"
                        />

                        <Radio
                            checked={opportunity.experienceLevel === '3-5 yrs'}
                            id="chkExperienceLevel35"
                            label="3-5 yrs"
                            name="experienceLevel"
                            onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                            value="3-5 yrs"
                        />

                        <Radio
                            checked={opportunity.experienceLevel === '5-10 yrs'}
                            id="chkExperienceLevel510"
                            label="5-10 yrs"
                            name="experienceLevel"
                            onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                            value="5-10 yrs"
                        />

                        <Radio
                            checked={opportunity.experienceLevel === '10 yrs+'}
                            id="chkExperienceLevel10plus"
                            label="10 yrs+"
                            name="experienceLevel"
                            onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                            value="10 yrs+"
                        />
                    </RadioGroup>
                </FormRow>

                <FormRow>
                    <Dropdown
                        closeOnSelect={false}
                        id="selIndustryExperience"
                        label="Industry Experience"
                        labelKey="name"
                        multi={true}
                        name="industryExperiences"
                        onChange={handleMultiSelectChange.bind(this, this._handleChange.bind(this), 'industryExperiences', null, null)}
                        options={this.props.industryExperienceTypes}
                        simpleValue={false}
                        value={opportunity.industryExperiences}
                        valueKey="id"
                    />
                </FormRow>

                <FormRow>
                    <ToggleSwitch
                        checked={opportunity.allowsRemote === true}
                        label="Can freelancer work remotely?"
                        labelPosition="top"
                        name="allowsRemote"
                        onChange={(event) => handleCheckboxChange(this._handleChange.bind(this), event)}
                    />
                </FormRow>

                {Object.keys(error).length > 0 ? (
                    <FormRow className="error-message">
                            <FormValidationMessage>
                                {error.friendlyMessage || error.message}
                            </FormValidationMessage>
                    </FormRow>
                ) : null}

                <FormRow className="button-wrapper button-wrapper--fixed">
                    <Button disabled={isSaving === true}
                            onClick={(event) => {
                                event.preventDefault();
                                this.props.$submit(onValid, onInvalid);
                            }}
                            type="submit">
                        {opportunity.published === true ? 'Save Changes' : 'Publish'}
                        {isSaving === true && opportunity.published === true ? <LoadingCircle innerColor="#ffffff" outerColor="#ffffff" /> : null}
                    </Button>

                    <Button className="a-button--primary"
                            disabled={isSaving === true}
                            onClick={(event) => {
                                event.preventDefault();
                                this.props.$submit(onValidDraft, onInvalidDraft);
                            }}
                            type="submit">
                        Save Draft
                        {isSaving === true && opportunity.published !== true ? <LoadingCircle innerColor="#ffffff" outerColor="#ffffff" /> : null}
                    </Button>

                    <Button className="a-button--cancel"
                            disabled={isSaving === true}
                            onClick={(event) => {
                                event.preventDefault();
                                this.props.handleCancel();
                            }}
                            type="button">
                        Cancel
                    </Button>
                </FormRow>
            </Form>
        );
    }
}

function editPostingFormValidationConfig(props) {
    const { description, specializations, startDate, summary, title } = props.opportunity;

    return {
        fields: ['description', 'specializations', 'startDate', 'summary', 'title'],
        validations: {
            description: [
                [isRequired, description]
            ],
            specializations: [
                [isRequired, specializations]
            ],
            startDate: [
                [isRequired, startDate]
            ],
            summary: [
                [isRequired, summary]
            ],
            title: [
                [isRequired, title]
            ]
        }
    }
}

export default EditPostingForm = validated(editPostingFormValidationConfig)(EditPostingForm);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/EditPosting/EditPostingForm.js